import React from 'react'
import styled, { css } from 'styled-components'
import { ThemeColors } from '../../new/theme/colors'
import { Box } from '../../new/core-components'
import { InputState } from './InputState'
import { Props as FieldProps } from './Field'

type Props = {
  children: React.ReactNode
  hoverColor?: ThemeColors
  inputState: InputState
  isFillBgOnHover?: boolean
  roundedBottomCorners?: boolean
} & FieldProps

const Placeholder = styled(Box)<Pick<Props, 'isFillBgOnHover' | 'inputState' | 'hoverColor'>>`
  transition: 0.2s;
  box-shadow: none;
  ${({ isFillBgOnHover, hoverColor, theme, inputState }) =>
    isFillBgOnHover && inputState === InputState.Idle
      ? css`
          &:hover {
            background: ${theme.colors[hoverColor ?? 'neutral-dark-4']};
          }
        `
      : ''}
  ${({ inputState, theme }) => {
    switch (inputState) {
      case InputState.Disabled: {
        return css`
          box-shadow: none;
          background: ${theme.colors['bg/disabled']};
          color: ${theme.colors['text/form-placeholder']};
        `
      }

      case InputState.Focused: {
        return css`
          background: ${theme.colors['bg/form-item-focused']};
        `
      }

      case InputState.Invalid: {
        return css`
          box-shadow: none;
          background: ${theme.colors['bg/form-item-focused-error']};
        `
      }
    }
  }}
`

Placeholder.displayName = 'Placeholder'

export const Container = ({
  inputState,
  children,
  isFillBgOnHover,
  small,
  h,
  hoverColor,
  roundedBottomCorners,
  focused,
  ...others
}: Props) => {
  return (
    <Placeholder
      inputState={inputState}
      minHeight={small ? 7 : 9}
      height={small ? 7 : h}
      display='flex'
      flexDirection='column'
      justifyContent='center'
      isFillBgOnHover={isFillBgOnHover}
      hoverColor={hoverColor}
      borderRadius={8}
      borderBottomLeftRadius={getBottomCornerRadius(inputState, roundedBottomCorners, focused)}
      borderBottomRightRadius={getBottomCornerRadius(inputState, roundedBottomCorners, focused)}
      boxShadow={inputState === InputState.Focused ? 'border/form-item-focused' : 'border/on-major'}
      bg='bg/major'
      {...others}
    >
      {children}
    </Placeholder>
  )
}

const getBottomCornerRadius = (
  inputState: InputState,
  roundedBottomCorners?: boolean,
  focused?: boolean
) => {
  if ((focused && inputState !== InputState.Invalid) || inputState === InputState.Focused) {
    return 0
  }
  if (roundedBottomCorners) {
    return 8
  }
  return undefined
}
